import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Copy, Check, ArrowRight, Bell, Clock, Sun, Moon,
  AlertTriangle, Settings, MessageSquare, Shield, Zap, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function DocsDailyBriefs() {
  const [copiedCode, setCopiedCode] = useState(null);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const notificationTypes = [
    { type: 'score_drop', desc: 'Health Score drops by 5+ points in a single day', severity: 'Critical', bypassQuiet: true },
    { type: 'low_inventory', desc: 'Product stock falls below reorder point', severity: 'High', bypassQuiet: false },
    { type: 'agent_approval', desc: 'Agent action requires manual approval', severity: 'High', bypassQuiet: false },
    { type: 'deal_milestone', desc: 'Deal reaches click milestones (100, 500, 1K, 5K)', severity: 'Low', bypassQuiet: false },
    { type: 'competitor_alert', desc: 'Competitor price change detected on tracked products', severity: 'Medium', bypassQuiet: false },
    { type: 'order_exception', desc: 'Order flagged for delivery delay, chargeback, or fraud', severity: 'Critical', bypassQuiet: true },
  ];

  return (
    <div className="max-w-4xl">
      {/* Hero */}
      <div className="mb-12">
        <Badge className="bg-[#3EF1C7]/10 text-[#3EF1C7] border-[#3EF1C7]/30 mb-4">
          Daily Briefs
        </Badge>
        <h1 className="text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Daily Briefs & Notifications
        </h1>
        <p className="text-lg text-[#94A3B8]">
          Configure automated daily summaries delivered to your messaging channels. Set quiet hours,
          escalation rules, and customize which metrics and alerts you receive.
        </p>
      </div>

      {/* What's in a Daily Brief */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Daily Brief Content
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <p className="text-[#94A3B8] mb-4">
              Your daily brief is an automated summary sent once per day at your configured time.
              It provides a snapshot of your store's performance and anything that needs attention.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Revenue Summary</span>
                  <p className="text-sm text-[#94A3B8]">Total revenue, comparison vs previous day/week, and average order value</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Order Count</span>
                  <p className="text-sm text-[#94A3B8]">Number of orders placed, fulfilled, and any exceptions flagged</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Agent Decisions</span>
                  <p className="text-sm text-[#94A3B8]">Summary of agent actions — auto-approved, manually approved, rejected, and pending</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Health Score Changes</span>
                  <p className="text-sm text-[#94A3B8]">Current score, change from yesterday, and component breakdown</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Alerts & Warnings</span>
                  <p className="text-sm text-[#94A3B8]">Low inventory warnings, expiring deals, delivery exceptions</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-white font-medium">Recommendations</span>
                  <p className="text-sm text-[#94A3B8]">AI-generated suggestions to improve performance, restock products, or optimize deals</p>
                </div>
              </li>
            </ul>
          </CardContent>
        </Card>
      </section>

      {/* Configuration */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Configuration
        </h2>
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">Delivery Time</h3>
                  <p className="text-sm text-[#94A3B8]">
                    Choose when your daily brief arrives. Default: <strong className="text-white">8:00 AM</strong> in
                    your configured timezone. Adjustable in 30-minute increments.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Sun className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">Timezone</h3>
                  <p className="text-sm text-[#94A3B8]">
                    Set your timezone so briefs and quiet hours are calibrated correctly. Supports all
                    IANA timezone identifiers (e.g., <code className="text-[#3EF1C7]">America/New_York</code>).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <MessageSquare className="w-5 h-5 text-sky-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">Delivery Platforms</h3>
                  <p className="text-sm text-[#94A3B8]">
                    Choose which messaging platforms receive the daily brief. You can send to all linked
                    platforms or limit to specific ones (e.g., Telegram only).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#0D1628] border-[#22304A]">
            <CardContent className="p-6">
              <div className="flex items-start gap-3">
                <Settings className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-semibold mb-1">Metrics to Include</h3>
                  <p className="text-sm text-[#94A3B8]">
                    Customize which sections appear in your brief. Toggle revenue, orders, agents,
                    health score, alerts, and recommendations individually.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="relative">
          <button
            onClick={() => copyToClipboard(`# Configure daily briefs via messaging commands

# Set delivery time (24h format)
/policy set brief_time 08:00

# Set timezone
/policy set timezone America/New_York

# Set delivery platforms (comma-separated)
/policy set brief_channels telegram,slack

# Enable/disable specific brief sections
/policy set brief_revenue true
/policy set brief_orders true
/policy set brief_agents true
/policy set brief_health_score true
/policy set brief_alerts true
/policy set brief_recommendations true

# Disable daily brief entirely
/policy set brief_enabled false

# View current brief configuration
/policy view brief`, 'brief-config')}
            className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
          >
            {copiedCode === 'brief-config' ? (
              <Check className="w-4 h-4 text-[#3EF1C7]" />
            ) : (
              <Copy className="w-4 h-4 text-[#64748B]" />
            )}
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Configure daily briefs via messaging commands

# Set delivery time (24h format)
/policy set brief_time 08:00

# Set timezone
/policy set timezone America/New_York

# Set delivery platforms (comma-separated)
/policy set brief_channels telegram,slack

# Enable/disable specific brief sections
/policy set brief_revenue true
/policy set brief_orders true
/policy set brief_agents true
/policy set brief_health_score true
/policy set brief_alerts true
/policy set brief_recommendations true

# Disable daily brief entirely
/policy set brief_enabled false

# View current brief configuration
/policy view brief`}</code></pre>
        </div>
      </section>

      {/* Quiet Hours */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Quiet Hours
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <Moon className="w-6 h-6 text-purple-400 flex-shrink-0 mt-0.5" />
              <p className="text-[#94A3B8]">
                Quiet hours suppress non-critical notifications during off-hours. Default:
                <strong className="text-white"> 10:00 PM to 7:00 AM</strong> in your timezone.
                Notifications that arrive during quiet hours are held and delivered in a batch
                when quiet hours end.
              </p>
            </div>
            <div className="relative">
              <button
                onClick={() => copyToClipboard(`# Set quiet hours (24h format, in your timezone)
/policy set quiet_hours 22:00-07:00

# Disable quiet hours (receive all notifications 24/7)
/policy set quiet_hours disabled

# View current quiet hours
/policy view quiet_hours`, 'quiet-hours')}
                className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
              >
                {copiedCode === 'quiet-hours' ? (
                  <Check className="w-4 h-4 text-[#3EF1C7]" />
                ) : (
                  <Copy className="w-4 h-4 text-[#64748B]" />
                )}
              </button>
              <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Set quiet hours (24h format, in your timezone)
/policy set quiet_hours 22:00-07:00

# Disable quiet hours (receive all notifications 24/7)
/policy set quiet_hours disabled

# View current quiet hours
/policy view quiet_hours`}</code></pre>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Escalation Rules */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Escalation Rules
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-6">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-[#94A3B8]">
                Critical alerts <strong className="text-white">bypass quiet hours</strong> and are delivered
                immediately regardless of the time. This ensures you are always notified about events
                that require urgent attention.
              </p>
            </div>
            <div className="bg-[#0B1120] border border-[#22304A] rounded-lg p-4">
              <p className="text-sm text-[#64748B] mb-3">Events that bypass quiet hours:</p>
              <ul className="space-y-2 text-sm text-[#94A3B8]">
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-rose-400"></span>
                  <span><strong className="text-white">score_drop</strong> — Health Score drops by 5+ points</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-rose-400"></span>
                  <span><strong className="text-white">order_exception</strong> — Delivery delay, chargeback, or fraud flag</span>
                </li>
              </ul>
              <p className="text-sm text-[#64748B] mt-3">All other notification types respect quiet hours.</p>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Notification Types */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Notification Types
        </h2>
        <Card className="bg-[#0D1628] border-[#22304A]">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#22304A]">
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Type</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Trigger</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Severity</th>
                  <th className="text-left text-[#94A3B8] p-4 font-medium">Bypasses Quiet</th>
                </tr>
              </thead>
              <tbody>
                {notificationTypes.map((n, i) => (
                  <tr key={n.type} className={i < notificationTypes.length - 1 ? 'border-b border-[#22304A]/50' : ''}>
                    <td className="p-4 text-[#3EF1C7] font-mono text-xs">{n.type}</td>
                    <td className="p-4 text-[#94A3B8]">{n.desc}</td>
                    <td className="p-4">
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        n.severity === 'Critical' ? 'bg-rose-500/10 text-rose-400' :
                        n.severity === 'High' ? 'bg-amber-500/10 text-amber-400' :
                        n.severity === 'Medium' ? 'bg-sky-500/10 text-sky-400' :
                        'bg-[#3EF1C7]/10 text-[#3EF1C7]'
                      }`}>{n.severity}</span>
                    </td>
                    <td className="p-4 text-[#94A3B8]">{n.bypassQuiet ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
        <p className="text-[#94A3B8] text-sm mt-3">
          Notifications of the same type are rate-limited to prevent spam. Default cooldown: 1 hour
          for non-critical, immediate for critical.
        </p>
      </section>

      {/* Customization via Policy */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-white mb-6">
          Customization via /policy Commands
        </h2>
        <div className="relative">
          <button
            onClick={() => copyToClipboard(`# Enable/disable specific notification types
/policy set notify_score_drop true
/policy set notify_low_inventory true
/policy set notify_agent_approval true
/policy set notify_deal_milestone false
/policy set notify_competitor_alert true
/policy set notify_order_exception true

# Set low inventory threshold (units)
/policy set low_stock_threshold 20

# Set score drop threshold (points)
/policy set score_drop_threshold 5

# Set notification cooldown (minutes)
/policy set notification_cooldown 60

# View all notification settings
/policy view notifications`, 'policy-notifications')}
            className="absolute top-2 right-2 p-2 rounded hover:bg-white/10 transition-colors z-10"
          >
            {copiedCode === 'policy-notifications' ? (
              <Check className="w-4 h-4 text-[#3EF1C7]" />
            ) : (
              <Copy className="w-4 h-4 text-[#64748B]" />
            )}
          </button>
          <pre className="bg-[#0B1120] text-[#E2E8F0] p-4 rounded-lg overflow-x-auto text-sm border border-[#22304A]"><code>{`# Enable/disable specific notification types
/policy set notify_score_drop true
/policy set notify_low_inventory true
/policy set notify_agent_approval true
/policy set notify_deal_milestone false
/policy set notify_competitor_alert true
/policy set notify_order_exception true

# Set low inventory threshold (units)
/policy set low_stock_threshold 20

# Set score drop threshold (points)
/policy set score_drop_threshold 5

# Set notification cooldown (minutes)
/policy set notification_cooldown 60

# View all notification settings
/policy view notifications`}</code></pre>
        </div>
      </section>

      {/* What's Next? */}
      <section>
        <h2 className="text-2xl font-semibold text-white mb-6">
          What's Next?
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Link to="/docs/commands">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">Messaging Commands <ArrowRight className="inline w-4 h-4" /></h3>
                <p className="text-sm text-[#94A3B8]">Full command reference for all channels</p>
              </CardContent>
            </Card>
          </Link>
          <Link to="/docs/api-integration">
            <Card className="bg-[#0D1628] border-[#22304A] hover:border-[#3EF1C7]/30 transition-colors cursor-pointer h-full">
              <CardContent className="p-4">
                <h3 className="text-white font-medium mb-2">API Integration <ArrowRight className="inline w-4 h-4" /></h3>
                <p className="text-sm text-[#94A3B8]">Connect non-Shopify platforms via the REST API</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </section>
    </div>
  );
}
